/**
 * The page at the end of an invitation link.
 *
 * The one screen in this console somebody reaches before they have an account. Everything
 * it does is decided by the link: which firm, which address, and whether the link is still
 * alive. The person supplies exactly one thing — a password nobody else will ever know,
 * including us — which is the whole reason this replaced handing over a generated one.
 *
 * The offer is fetched before the form is shown rather than after it is submitted. A link
 * that has lapsed or has already been used should say so on arrival, not after somebody has
 * chosen a password and typed it twice.
 *
 * The same centred card as the sign-in screen, and like it this renders before
 * `/api/tenant/branding` has answered: it themes from the stored preference alone.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiError, errorKey, request, setTenant, type Schema } from '@taxi/api-client';

import { useAuth } from '../auth/authContext';
import { SecondFactor, type MfaChallenge } from '../auth/SecondFactor';
import { Banner, Button, Card, EmptyState, Field, Input, PageHeader } from '../ui';

type Offer = Schema<'InvitationOffer'>;

/** What the link says, read once on arrival. */
function readLink(): { firm: string; token: string } {
  const parameters = new URLSearchParams(window.location.search);

  return {
    firm: parameters.get('firm') ?? '',
    token: parameters.get('token') ?? '',
  };
}

export function AcceptInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { acceptInvitation, completeSecondFactor } = useAuth();

  const [link] = useState(readLink);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * The second factor, when the person being invited owes one.
   *
   * An administrator or a dispatcher does, and this is where they meet it: accepting the
   * invitation *is* their first sign-in, so enrolling here rather than on some later visit
   * is what makes it mandatory rather than a nag.
   */
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  function say(failure: unknown, fallback: string): string {
    return failure instanceof ApiError
      ? t(errorKey(failure.problem), {
          ...failure.problem.arguments,
          defaultValue: t(fallback),
        })
      : t(fallback);
  }

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (link.firm === '' || link.token === '') {
        setProblem(t('errors.invite.invalid'));
        setLoading(false);
        return;
      }

      try {
        // The company code has to be on the request before anything under /api answers:
        // tenant resolution runs in front of every endpoint, invitations included.
        setTenant(link.firm);

        const fetched = await request<Offer>(
          `/api/invitations/${encodeURIComponent(link.token)}`,
        );

        if (!cancelled) setOffer(fetched);
      } catch (failure) {
        if (!cancelled) setProblem(say(failure, 'errors.invite.invalid'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link.firm, link.token]);

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (password !== again) {
      setProblem(t('invite.mismatch'));
      return;
    }

    setBusy(true);
    setProblem(null);

    try {
      const owed = await acceptInvitation(link.firm, link.token, password);

      // The token is spent either way, so the link must not stay in the address bar: a
      // reload would replay a dead invitation and show a refusal to somebody who is
      // halfway through — or already — signed in.
      //
      // But it can only be cleared once there is nothing left on this screen to show.
      // `App` decides what renders from the path, so leaving `/invite` while a second
      // factor is still owed unmounts this component — and the challenge, the QR code and
      // the recovery codes go with it, on the one visit where enrolment is mandatory.
      if (owed === null) {
        void navigate('/', { replace: true });
        return;
      }

      setChallenge(owed);
    } catch (failure) {
      setProblem(say(failure, 'invite.couldNotAccept'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-ink-3 text-center p-10">{t('app.loading')}</p>;
  }

  if (challenge !== null) {
    return (
      <SecondFactor
        challenge={challenge}
        prefix="/api/auth"
        onSignedIn={(session) => {
          // Now, rather than when the password was set: this is the moment there is a
          // session, and therefore the first moment the dead link can be dropped without
          // taking the enrolment screen down with it.
          completeSecondFactor(session);
          void navigate('/', { replace: true });
        }}
        // Starting again means signing in, not filling this form in a second time: the
        // password is already set and the invitation token is already spent. The server
        // will owe the same enrolment at the next sign-in, so nothing is skipped.
        onCancel={() => {
          setChallenge(null);
          void navigate('/', { replace: true });
        }}
      />
    );
  }

  return (
    <main className="min-h-screen grid place-items-center p-6">
      <Card className="w-[min(360px,100%)]">
        <form className="flex flex-col gap-3.5 p-6" onSubmit={(event) => void submit(event)}>
          <PageHeader
            title={t('invite.title')}
            description={
              offer === null
                ? undefined
                : t('invite.intro', { firm: offer.tenantSlug, email: offer.email })
            }
          />

          {offer === null ? (
            <>
              <Banner tone="danger">{problem ?? t('errors.invite.invalid')}</Banner>

              {/* The only useful next step, and it is not one this page can take: whoever
                  invited them has to send another link. Saying so beats a dead end. */}
              <EmptyState>{t('invite.askForAnother')}</EmptyState>
            </>
          ) : (
            <>
              {problem !== null && <Banner tone="danger">{problem}</Banner>}

              <Field label={t('invite.password')} help={t('invite.passwordHint')}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={12}
                />
              </Field>

              <Field label={t('invite.again')}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={again}
                  onChange={(event) => setAgain(event.target.value)}
                  required
                  minLength={12}
                />
              </Field>

              <Button type="submit" variant="primary" disabled={busy} className="w-full">
                {busy ? t('invite.working') : t('invite.submit')}
              </Button>
            </>
          )}
        </form>
      </Card>
    </main>
  );
}
